import React, {useContext, useEffect, useState} from 'react';
import AccountSettingsUserDetails from "./AccountSettingsUserDetails";
import AccountSettingsSubscriptions from "./AccountSettingsSubscriptions";
import {Card, Form} from "antd";
import Alert from "@common/Alert";
import AccountSettingsClassesSchoolYear from "./AccountSettingsClassesSchoolYear";
import AccountSettingsClassKey from "./AccountSettingsClassKey";
import AccountSettingsChangePassword from "./AccountSettingsChangePassword";
import AccountSettingsRenewProduct from "./AccountSettingsRenewProduct";
import axios from "@common/axios";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";
import Button from "@common/Button";
import dayjs from "dayjs";

export const PAYMENT_SUCCESS = 'paymentSuccess';

const AccountSettings = () => {
    const [loadData, setLoadData] = useState(null);
    const hasPaymentSuccess = new URLSearchParams(window.location.search).has(PAYMENT_SUCCESS);
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);

    useEffect(() => {
        axios.get('/web/ui/accountSettings').then(response => {
            setLoadData(response.data);
        });

        const observer = new MutationObserver((mutationsList, observer) => {
            for (let mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    const recaptchaBadge = document.getElementsByClassName('grecaptcha-badge');
                    if (recaptchaBadge.length > 0) {
                        recaptchaBadge[0].style.bottom = '140px';
                        observer.disconnect();
                    }
                }
            }
        });
        observer.observe(document.body, {childList: true, subtree: true});
    }, []);

    const handleCloseSuccess = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete(PAYMENT_SUCCESS);
        window.history.replaceState({}, '', url);
    };

    const handleSubmitForm = (form, formData, initialFormData, url, successCallback, errorCallback) => {
        const isChanged = !_.isEqual(formData, initialFormData);
        if (isChanged) {
            form.validateFields()
                .then(() => {
                    axios.post(url, formData).then(response => {
                        if (response.data && response.data?.errors) {
                            const serverErrors = response.data.errors;
                            const formattedErrors = Object.keys(serverErrors).map(field => ({
                                name: field,
                                errors: [serverErrors[field]]
                            }));
                            form.setFields(formattedErrors);
                            const toastError = response.data.errors?.toast;
                            if (toastError) if (toastError) displayToastMessage('Error', toastError, 'error', undefined, 10000);
                            if (errorCallback && typeof errorCallback === 'function') errorCallback(response.data.errors);
                        } else if (response.data && response.data.success) {
                            if (successCallback && typeof successCallback === 'function') successCallback();
                        }
                    }).catch(error => {
                        if (errorCallback && typeof errorCallback === 'function') errorCallback();
                    }).finally(() => {
                        console.log('FINALY!!!');
                    });
                })
        } else {
            console.log('no change');
            if (errorCallback && typeof errorCallback === 'function') errorCallback();
        }
    };

    const daysUntilExpiry = user?.accountExpiringSoon && user?.expiryDate
        ? dayjs(user.expiryDate).diff(dayjs().startOf('day'), 'day')
        : null;

    return (
        <div className={'ch-account-settings-page'}>
            <h2>Account settings</h2>
            {hasPaymentSuccess && (
                <Alert
                    className="margin-t--l"
                    showIcon
                    closable
                    afterClose={handleCloseSuccess}
                    type="success"
                    message="Success"
                    description="Your payment was successful and your product has been added to your account. You will receive a receipt for your payment to the email address on your account."
                />
            )}
            {user.accountExpiringSoon && daysUntilExpiry > 0 &&
                <Alert
                    className="margin-t--l"
                    showIcon
                    type="warning"
                    description={`Your subscription will expire in ${daysUntilExpiry} days. ${user.student && user.schoolId != null ? brandConfig?.brandText.schoolStudentProductRenewal : brandConfig?.brandText.productRenewal}`}
                />
            }
            {user.accountExpiringSoon && daysUntilExpiry <= 0 &&
                <Alert
                    className="margin-t--l"
                    showIcon
                    type="warning"
                    description={`Your subscription has expired. ${user.student && user.schoolId != null ? brandConfig?.brandText.schoolStudentProductRenewal : brandConfig?.brandText.productRenewal}`}
                />
            }
            <Card>
                <AccountSettingsUserDetails
                    userDetails={loadData?.user}
                    data={loadData?.userDetails}
                    checkFormStatus={(form) => checkFormStatus(form)}
                    handleSubmitForm={handleSubmitForm}
                />
            </Card>
            {user.allowEditUser && (
                <Card>
                    <AccountSettingsChangePassword
                        data={loadData?.password}
                        checkFormStatus={(form) => checkFormStatus(form)}
                        handleSubmitForm={handleSubmitForm}
                    />
                </Card>
            )}
            <h2>Platform settings</h2>
            {user.role !== "SCHOOL_ADMIN" || user.role !== "ADMIN" && (
                <Alert
                    className="margin-t--l"
                    showIcon
                    description={
                        <>
                            <p>Some details can only be changed by your school administrator.</p>
                        </>
                    }
                />
            )}
            <Card>
                <AccountSettingsClassesSchoolYear
                    userDetails={loadData?.user}
                    data={loadData?.school}
                    checkFormStatus={(form) => checkFormStatus(form)}
                    handleSubmitForm={handleSubmitForm}
                />
            </Card>
            <Card>
                <AccountSettingsSubscriptions
                    userDetails={loadData?.user}
                />
            </Card>
            {(user?.brand.renewByActivationCode || user?.brand.registerByCreditCard) && user.accountExpiringSoon &&
                <Card>
                    <AccountSettingsRenewProduct
                        data={loadData?.products}
                        checkFormStatus={(form) => checkFormStatus(form)}
                        handleSubmitForm={handleSubmitForm}
                    />
                </Card>
            }
            {user?.brand.classKeyReq && (!brandConfig.filterUserByAuthority || user.authorityId == 1) &&
                <Card>
                    <AccountSettingsClassKey
                        checkFormStatus={(form) => checkFormStatus(form)}
                        handleSubmitForm={handleSubmitForm}
                    />
                </Card>
            }
        </div>
    )
}

const checkFormStatus = (form) => {
    const formValues = form.getFieldsValue(true);
    const formErrors = form.getFieldsError();
    const requiredFields = Object.keys(formValues).filter(field => {
        const fieldMeta = form.getFieldInstance?.(field);
        if (!fieldMeta) return false;
        return fieldMeta.nativeElement?.ariaRequired;
    });
    const allRequiredFilled = requiredFields.every(field => {
        const value = form.getFieldValue(field);
        return value !== undefined && value !== null && value !== '';
    });
    const hasErrors = formErrors.some(error => error.errors.length > 0);
    return allRequiredFilled && !hasErrors;
}

export default AccountSettings;